import { useState, useEffect } from "react";
import { MessageCircleWarning, Bug, Lightbulb, MessageSquare, Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import HelpDrawer from "@/components/layout/HelpDrawer";

const CATEGORIES = [
  { value: "bug", label: "Bug", icon: Bug, color: "text-destructive" },
  { value: "suggestion", label: "Suggestion", icon: Lightbulb, color: "text-amber-500" },
  { value: "other", label: "Autre", icon: MessageSquare, color: "text-muted-foreground" },
] as const;

/** Imperative open handle — singleton set by FloatingReportButton */
let _openReportDialog: (() => void) | null = null;
export function openReportDialog() {
  _openReportDialog?.();
}

export default function FloatingReportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();

  // Register imperative opener
  useEffect(() => {
    _openReportDialog = () => setOpen(true);
    return () => { _openReportDialog = null; };
  }, []);

  if (!user) return null;

  const handleSubmit = async () => {
    if (!category || !message.trim()) return;
    setSubmitting(true);

    const { error } = await supabase.from("reports").insert({
      created_by: user.id,
      target_type: "app",
      target_id: "global",
      reason: category,
      details: message.trim(),
      status: "new",
    });

    setSubmitting(false);

    if (error) {
      console.error("[Report]", error);
      toast.error("Erreur lors de l'envoi. Réessaye.");
      return;
    }

    toast.success("Signalement envoyé. Merci !");
    setCategory("");
    setMessage("");
    setOpen(false);
  };

  return (
    <>
      {/* Floating button — desktop only */}
      {!isMobile && (
        <div className="fixed bottom-14 right-4 z-50 flex flex-col items-center gap-2">
          <HelpDrawer />
          <button
            onClick={() => setOpen(true)}
            className={cn(
              "flex items-center justify-center rounded-full",
              "h-8 w-8",
              "bg-card/80 border border-border",
              "hover:bg-primary/15",
              "transition-all duration-300",
            )}
            title="Signaler un bug ou une suggestion"
          >
            <MessageCircleWarning className="h-4 w-4 text-primary/70 hover:text-primary transition-colors" />
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <MessageCircleWarning className="h-5 w-5 text-primary" />
              Signaler un problème
            </DialogTitle>
            <DialogDescription className="text-xs">
              Un bug, une idée ou un doute ? L'admin sera notifié.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const selected = category === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={cn(
                      "flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-xs font-medium transition-all",
                      selected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card hover:border-primary/30"
                    )}
                  >
                    <Icon className={cn("h-5 w-5", selected ? "text-primary" : cat.color)} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Décris le problème ou ta suggestion…"
              maxLength={1000}
              className="bg-secondary border-border resize-none min-h-[80px]"
              rows={3}
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!category || !message.trim() || submitting}
                className="gap-1.5"
              >
                {submitting ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi…</>
                ) : (
                  <><Send className="h-3.5 w-3.5" /> Envoyer</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
